import { setup } from 'xstate';

import { PaymentStatePollingActions } from './payment-state-polling-machine.actions';
import { PaymentStatePollingActors } from './payment-state-polling-machine.actors';
import {
  PaymentStatePollingContext,
  initialContext,
} from './payment-state-polling-machine.context';
import { PaymentStatePollingDelays } from './payment-state-polling-machine.delays';
import { PaymentStatePollingGuards } from './payment-state-polling-machine.guards';

// ─────────────────────────────────────────────────────────────────────────────

export const paymentStatePollingMachine = setup({
  types: {
    context: {} as PaymentStatePollingContext,
    input: {} as Partial<PaymentStatePollingContext>,
  },
  actions: PaymentStatePollingActions,
  actors: PaymentStatePollingActors,
  guards: PaymentStatePollingGuards,
  delays: PaymentStatePollingDelays,
}).createMachine({
  id: 'payment-state-polling',
  context: ({ input }) => ({ ...initialContext, ...input }),
  type: 'parallel',
  on: {
    START: {
      target: ['#payment-state-polling', '#payment-state-delayed-waiting'],
      actions: ['onStartPolling'],
    },
    RESET: {
      target: ['#payment-state-idle', '#payment-state-delayed-idle'],
    },
    CREATE_PAYMENT: {
      actions: ['onCreatePayment'],
    },
    CLEAR_PAYMENT_STATE: {
      actions: ['clearPaymentState'],
    },
    CANCEL_PAYMENT: [
      {
        target: '#payment-state-canceling',
        guard: 'checkIfPaymentIsCancelable',
      },
      {
        target: ['#payment-state-idle', '#payment-state-delayed-idle'],
        actions: ['clearPendingPayment', 'incrementPurchaseAttempt'],
      },
    ],
    FAIL_PAYMENT: {
      target: ['#payment-state-idle', '#payment-state-delayed-idle'],
      actions: ['clearPendingPayment', 'incrementPurchaseAttempt'],
    },
  },
  states: {
    polling: {
      initial: 'idle',
      states: {
        idle: {
          id: 'payment-state-idle',
        },
        waiting: {
          id: 'payment-state-waiting',
          after: {
            paymentStatePolling: '#payment-state-polling',
          },
        },
        polling: {
          id: 'payment-state-polling',
          invoke: {
            id: 'fetchPaymentState',
            src: 'fetchPaymentState',
            input: ({ context }) => context,
            onDone: {
              target: '#payment-state-checking',
              actions: ['onPaymentStateFetched'],
            },
            onError: ['#payment-state-idle', '#payment-state-delayed-idle'],
          },
        },
        checking: {
          id: 'payment-state-checking',
          always: [
            {
              target: '#payment-state-fulfilled',
              actions: ['onPaymentFulfilled', 'clearPendingPayment'],
              guard: 'checkIfPaymentStateFulfilled',
            },
            {
              target: '#payment-state-error',
              actions: ['clearPendingPayment', 'incrementPurchaseAttempt', 'onPaymentStateError'],
              guard: 'checkIfPaymentStateFailed',
            },
            {
              target: '#payment-state-waiting',
              guard: 'checkIfPaymentIsPending',
            },
            {
              target: ['#payment-state-idle', '#payment-state-delayed-idle'],
              actions: ['clearPendingPayment'],
            },
          ],
        },
        canceling: {
          id: 'payment-state-canceling',
          invoke: {
            id: 'cancelPayment',
            src: 'cancelPayment',
            input: ({ context }) => ({ paymentIntentId: context.paymentState?.paymentIntentId }),
            onDone: {
              target: ['#payment-state-idle', '#payment-state-delayed-idle'],
              actions: ['onPaymentCanceled', 'clearPendingPayment', 'incrementPurchaseAttempt'],
            },
            onError: {
              target: ['#payment-state-idle', '#payment-state-delayed-idle'],
              actions: ['onPaymentCanceled', 'clearPendingPayment', 'incrementPurchaseAttempt'],
            },
          },
        },
        error: {
          id: 'payment-state-error',
        },
        fulfilled: {
          id: 'payment-state-fulfilled',
        },
      },
    },
    delayed: {
      initial: 'idle',
      states: {
        idle: {
          id: 'payment-state-delayed-idle',
        },
        waiting: {
          id: 'payment-state-delayed-waiting',
          after: {
            paymentStateDelayedTolerance: {
              target: '#payment-state-delayed-delayed',
            },
          },
        },
        delayed: {
          id: 'payment-state-delayed-delayed',
        },
      },
    },
  },
});
